import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { extractApiError } from "@/lib/api";

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Resolve any backend image reference into a renderable absolute URL.
 *  - Absolute http(s) URLs pass through.
 *  - Relative "/api/..." paths get prefixed with REACT_APP_BACKEND_URL.
 *  - storage paths (e.g. "azure-gypsy-farms/images/abc.jpg") get prefixed with /api/files/public/.
 */
export const resolveImageUrl = (val) => {
  if (!val) return "";
  if (/^https?:\/\//i.test(val)) return val;
  if (val.startsWith("/api/")) return `${API_ORIGIN}${val}`;
  if (val.startsWith("/")) return `${API_ORIGIN}${val}`;
  return `${API_ORIGIN}/api/files/public/${val}`;
};

/**
 * ImageUploader — drop-in replacement for an image URL input.
 * - Lets the user pick a local file → uploads to /api/uploads/image → returns the public URL via onChange.
 * - Shows preview if a value is present.
 * - Also keeps a manual URL field for users who prefer pasting a link.
 */
export default function ImageUploader({ value, onChange, label = "Image", testid = "image-uploader" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large (max 8 MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(extractApiError(err, "Upload failed."));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={testid} className="space-y-2">
      {label && <div className="text-xs font-semibold text-stone-800">{label}</div>}
      {value ? (
        <div className="relative w-full">
          <img
            src={resolveImageUrl(value)}
            alt=""
            className="w-full max-h-48 object-cover rounded-md border border-stone-200 bg-stone-100"
          />
          <button
            type="button"
            onClick={() => onChange?.("")}
            data-testid={`${testid}-clear`}
            className="absolute top-2 right-2 bg-stone-900/75 hover:bg-stone-900 text-stone-50 rounded-full p-1.5"
            aria-label="Remove image"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="border-2 border-dashed border-stone-300 rounded-md p-6 text-center bg-stone-50">
          <ImageIcon className="mx-auto text-stone-400" size={22} strokeWidth={1.5} />
          <p className="mt-2 text-xs text-stone-500 font-sans">JPG, PNG or WEBP · max 8 MB</p>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testid}-pick`}
          className="bg-emerald-800 hover:bg-emerald-700 text-stone-50"
        >
          <Upload size={14} className="mr-1.5" />
          {uploading ? "Uploading…" : value ? "Replace photo" : "Upload photo"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onPick}
          className="hidden"
          data-testid={`${testid}-input`}
        />
      </div>
    </div>
  );
}
